/**
 * Product reviews (OMA)
 *
 * 1. Highlighted-reviews carousel: a scroll-snap track with prev/next arrows on
 *    desktop and swipe on mobile. Arrows disable at the ends and hide entirely
 *    when everything already fits. Degrades to a plain scroller without JS.
 *
 * 2. "All reviews" modal: the shell (open/close/focus-trap) is the theme's
 *    <modal-dialog> (global.js); the review list itself is the embedded live
 *    Judge.me widget. This file only:
 *      - hard-locks page scroll on <html> while the modal is open (the theme's
 *        body `overflow: hidden` alone doesn't stop the root from scrolling), and
 *      - nudges a resize on open so the Judge.me widget lays out at full width
 *        (it renders while the modal is display:none / zero-width).
 */
(function () {
  'use strict';

  var REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function trackGap(track) {
    var gap = parseFloat(window.getComputedStyle(track).columnGap || '0');
    return isNaN(gap) ? 0 : gap;
  }

  /* --------------------------------------------------------------- carousel */

  function initCarousel(section) {
    var carousel = section.querySelector('[data-reviews-carousel]');
    if (!carousel) return;

    var track = carousel.querySelector('[data-reviews-track]');
    var prev = carousel.querySelector('[data-reviews-prev]');
    var next = carousel.querySelector('[data-reviews-next]');
    if (!track) return;

    function step() {
      var card = track.querySelector('.product-reviews-oma__card');
      if (!card) return track.clientWidth;
      return card.getBoundingClientRect().width + trackGap(track);
    }

    function scrollByStep(dir) {
      track.scrollBy({ left: dir * step(), behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    }

    function setDisabled(btn, disabled) {
      if (!btn) return;
      btn.disabled = disabled;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    function updateArrows() {
      var scrollable = track.scrollWidth - track.clientWidth > 1;
      // Arrows stay visible (per the design); only dim them at the real ends,
      // and only when there is actually something to scroll.
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      setDisabled(prev, scrollable && atStart);
      setDisabled(next, scrollable && atEnd);
    }

    if (prev) prev.addEventListener('click', function () { scrollByStep(-1); });
    if (next) next.addEventListener('click', function () { scrollByStep(1); });

    var raf;
    track.addEventListener('scroll', function () {
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(updateArrows);
    });
    window.addEventListener('resize', updateArrows);

    updateArrows();
  }

  /* ------------------------------------------------------------------ modal */

  function syncScrollLock() {
    var anyOpen = !!document.querySelector('.reviews-modal[open]');
    document.documentElement.classList.toggle('reviews-modal-open', anyOpen);
    return anyOpen;
  }

  function initModal(modal) {
    // Scroll-lock while open, and nudge Judge.me / responsive embeds to lay out
    // at the modal's width once it becomes visible.
    var observer = new MutationObserver(function () {
      syncScrollLock();
      if (modal.hasAttribute('open')) {
        // Judge.me lays out / lazy-loads review photos responsively; nudge it
        // now that the widget is visible at the modal's width.
        window.requestAnimationFrame(function () {
          window.dispatchEvent(new Event('resize'));
          window.dispatchEvent(new Event('scroll'));
        });
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });

    relocateWidget(modal);
  }

  // Judge.me renders the review widget into its off-screen host (it won't render
  // inside the display:none modal, and doesn't re-scan for markup injected
  // later). Move that live, already-rendered widget into the modal body as soon
  // as Judge.me has populated it — NOT gated on the modal opening, so we never
  // race Judge.me's async render.
  function relocateWidget(modal) {
    var target = modal.querySelector('[data-reviews-widget-target]');
    if (!target) return;

    var sectionId = modal.id.replace('ReviewsModal-', '');
    var host =
      document.querySelector('[data-reviews-widget-host="' + sectionId + '"]') ||
      document.querySelector('[data-reviews-widget-host]');
    if (!host) return;

    // Judge.me is done when its rendered widget (.jdgm-rev-widg) exists, or the
    // placeholder has real content — not just our empty .jdgm-widget shell.
    function ready() {
      if (host.querySelector('.jdgm-rev-widg')) return true;
      var jd = host.querySelector('#judgeme_product_reviews');
      return !!jd && jd.innerHTML.trim().length > 50;
    }

    function move() {
      if (target.dataset.filled === 'true') return true;
      if (!ready()) return false;
      while (host.firstChild) target.appendChild(host.firstChild);
      target.dataset.filled = 'true';
      window.requestAnimationFrame(function () {
        window.dispatchEvent(new Event('resize'));
      });
      return true;
    }

    // Move now if Judge.me already rendered; otherwise watch the host until it does.
    if (move()) return;
    var hostObserver = new MutationObserver(function () {
      if (move()) hostObserver.disconnect();
    });
    hostObserver.observe(host, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------- badge → reviews jump */

  // The Judge.me preview badge (.jdgm-prev-badge) is injected by the app near
  // the product title — asynchronously, and possibly after this script runs.
  // Judge.me binds its own click handler that jumps to the live Judge.me
  // widget, which we relocate off-screen / into the modal. Intercept the click
  // in the CAPTURE phase (delegated, so it survives the async render) — this
  // runs before Judge.me's bubble handler — then preventDefault +
  // stopPropagation so Judge.me never scrolls, and send the page to our own
  // visible reviews section instead.
  var badgeScrollBound = false;

  function initBadgeScroll() {
    if (badgeScrollBound) return;
    badgeScrollBound = true;

    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        var badge = target && target.closest && target.closest('.jdgm-prev-badge');
        if (!badge) return;

        var section = document.querySelector('.product-reviews-oma');
        if (!section) return;

        event.preventDefault();
        event.stopPropagation();
        section.scrollIntoView({
          behavior: REDUCED_MOTION ? 'auto' : 'smooth',
          block: 'start',
        });
      },
      true
    );
  }

  /* -------------------------------------------------------------- bootstrap */

  function initSection(section) {
    if (!section || section.dataset.reviewsInit === 'true') return;
    section.dataset.reviewsInit = 'true';
    initCarousel(section);
  }

  function initAll() {
    document.querySelectorAll('.product-reviews-oma[data-section-id]').forEach(initSection);
    // Modals are moved to <body> by <modal-dialog>; init each once.
    document.querySelectorAll('.reviews-modal').forEach(function (modal) {
      if (modal.dataset.reviewsInit === 'true') return;
      modal.dataset.reviewsInit = 'true';
      initModal(modal);
    });
    initBadgeScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: markup is replaced on section reload.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.product-reviews-oma[data-section-id]');
    if (section) {
      section.dataset.reviewsInit = '';
      initSection(section);
    }
    initAll();
  });
})();
